// AiFeedItemMessage.jsx

import React, { useMemo, useState } from 'react';

// Context
import { useTheme } from 'context/ThemeContext';

// Utilities
import { formatChannelRelativeTime } from 'channels/utils/channelTime';

// Constants
import { AI_TITLE } from 'spaces/constants/aiConstants';
import { AI_FEED_CATEGORY_OPTIONS } from 'spaces/constants/aiFeedConstants';

// CSS
import './AiFeedItemMessage.css';
import './AiSurfaceMessage.css';

// Components
import AiFormattedText from 'spaces/components/AiFormattedText';
import AiMessageActions from 'spaces/components/AiMessageActions';
import AiThreadChrome from 'spaces/components/AiThreadChrome';

const CATEGORY_LABEL_BY_KEY = AI_FEED_CATEGORY_OPTIONS.reduce((acc, option) => {
  acc[option.key] = option.label;
  return acc;
}, {
  return_digest: 'Catch-up',
});

function formatFeedItemTime(createdAtMs) {
  const value = Number(createdAtMs);
  if (!Number.isFinite(value) || value <= 0) return '';

  return formatChannelRelativeTime(new Date(value));
}

function FeedMessageQuote({ text, theme }) {
  const cleanText = String(text || '').trim();
  if (!cleanText) return null;

  // ----- RENDER ----- //

  return (
    <blockquote
      className="ai-feed-item-message__quote"
    >
      {cleanText}
    </blockquote>
  );
}

const AiFeedItemMessage = React.memo(({
  message,
  promptText = '',
  canReplyInThread = false,
  hasUnreadReplies = false,
  onOpenThread,
  onReplyInThread,
  threadSummariesByRootKey = {},
}) => {
  // ----- CONTEXT ----- //

  const { theme } = useTheme();
  // ----- STATE ----- //

  const [expanded, setExpanded] = useState(false);
  const createdLabel = formatChannelRelativeTime(message?.dateCreated);
  const feed = message?.feed && typeof message.feed === 'object' ? message.feed : null;
  const isSocialDm = String(feed?.social?.actionKind || '').trim() === 'share.text'
    || String(feed?.sourceType || '').trim() === 'SOCIAL_DM';
  const categoryLabel = isSocialDm
    ? 'DM'
    : (CATEGORY_LABEL_BY_KEY[String(feed?.category || '').trim()] || 'Update');
  // ----- MEMOS ----- //

  const groupedItems = useMemo(
    () => (Array.isArray(feed?.items) ? feed.items : []).filter((item) => String(item?.text || '').trim()),
    [feed?.items],
  );
  const isGrouped = groupedItems.length > 1;
  const visibleItems = isGrouped && expanded ? groupedItems : [];
  const messagePreview = String(feed?.messagePreview || '').trim();

  // ----- RENDER ----- //

  return (
    <div
      className={[
        'ai-feed-item-message',
        isSocialDm ? 'ai-feed-item-message--social-dm' : '',
      ].filter(Boolean).join(' ')}
      id={`message-${message?.key || ''}`}
      data-feed-category={feed?.category || ''}
      data-social-dm={isSocialDm ? 'true' : undefined}
    >

      {/* AVATAR */}
      <div
        className="ai-feed-item-message__avatar"
        aria-hidden
      >
        AI
      </div>

      {/* BODY */}
      <div className="ai-feed-item-message__body">

        {/* HEADER */}
        <div className="ai-feed-item-message__header">

          {/* NAME */}
          <span
            className="ai-feed-item-message__name"
          >
            {AI_TITLE}
          </span>

          {/* CHIP */}
          <span
            className="ai-feed-item-message__chip"
          >
            {categoryLabel}
          </span>

          {/* TIME */}
          {createdLabel ? (
            <span
              className="ai-feed-item-message__time"
            >
              {createdLabel}
            </span>
          ) : null}
        </div>

        {/* TEXT */}
        <div
          className="ai-feed-item-message__text"
        >
          <AiFormattedText
            text={message?.text || ''}
            keyPrefix={isGrouped ? 'summary' : 'text'}
          />
        </div>

        {!isGrouped && messagePreview ? (
          <FeedMessageQuote text={messagePreview} theme={theme} />
        ) : null}

        {/* EXPAND */}
        {isGrouped ? (
          <button
            className="ai-feed-item-message__expand"
            type="button"
            onClick={() => setExpanded((current) => !current)}
          >
            {expanded ? 'Hide updates' : `Show ${groupedItems.length} updates`}
          </button>
        ) : null}

        {visibleItems.length ? (
          <ul className="ai-feed-item-message__group-list">
            {visibleItems.map((item, index) => {
              const itemKey = String(item?.notificationKey || item?.createdAtMs || index);
              const itemTime = formatFeedItemTime(item?.createdAtMs);

              return (
                <li
                  className="ai-feed-item-message__group-item"
                  style={{
                    borderColor: theme.border,
                  }}
                  key={itemKey}
                >

                  {/* GROUP ITEM TEXT */}
                  <div className="ai-feed-item-message__group-item-text">
                    <AiFormattedText text={item.text} keyPrefix={itemKey} />
                  </div>
                  {item?.messagePreview ? (
                    <FeedMessageQuote text={item.messagePreview} theme={theme} />
                  ) : null}

                  {/* GROUP ITEM META */}
                  {itemTime ? (
                    <div
                      className="ai-feed-item-message__group-item-meta"
                    >
                      <span>{itemTime}</span>
                    </div>
                  ) : null}
                </li>
              );
            })}
          </ul>
        ) : null}

        <AiMessageActions message={message} />

        <AiThreadChrome
          message={message}
          promptText={promptText}
          canReplyInThread={canReplyInThread}
          hasUnreadReplies={hasUnreadReplies}
          threadSummariesByRootKey={threadSummariesByRootKey}
          onOpenThread={onOpenThread}
          onReplyInThread={onReplyInThread}
        />
      </div>
    </div>
  );
});

// Dev Tools
AiFeedItemMessage.displayName = 'AiFeedItemMessage';

export default AiFeedItemMessage;
